import { normalizeTimeZone } from '@kirin/api-client/helpers/time-zone';
import { safeLocalStorage } from '@/lib/safeLocalStorage';

const STORAGE_KEY = 'ui.time-zone';
export const DEFAULT_TIME_ZONE = 'UTC';

function readSavedTimeZone(): string {
  return (
    normalizeTimeZone(safeLocalStorage.getItem(STORAGE_KEY)) ??
    DEFAULT_TIME_ZONE
  );
}

// An external store rather than a React context: no provider to mount, and
// components subscribe through `useDisplayTimeZone`, so a change re-renders
// them in place.
let current = readSavedTimeZone();
const listeners = new Set<() => void>();

function update(tz: string): void {
  if (tz === current) return;
  current = tz;
  listeners.forEach(listener => listener());
}

// Follow a change made in another tab (`key` is null when storage is cleared),
// always, so a page opened later in this tab shows it. A pick whose save failed
// (storage blocked) raises no event, so it stays. Guarded so importing this
// outside a browser (a Node-environment test, a script) doesn't throw.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.key === STORAGE_KEY || event.key === null) {
      update(readSavedTimeZone());
    }
  });
}

/**
 * The per-user display time zone (Settings → Preferences → Time Zone). Every
 * date + time the app renders through `formatDateTime` is shown in this zone.
 * Defaults to UTC. Stored per browser, like the Timestamp Format setting.
 */
export function getDisplayTimeZone(): string {
  return current;
}

/**
 * Saves `tz` as the display zone; ignores one `Intl` cannot format in, or a
 * bare offset.
 */
export function setDisplayTimeZone(tz: string): void {
  const zone = normalizeTimeZone(tz);
  if (!zone) return;
  safeLocalStorage.setItem(STORAGE_KEY, zone);
  update(zone);
}

/** Calls `listener` after every display-zone change; returns the unsubscribe. */
export function subscribeDisplayTimeZone(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** `tz` as people read it: `America/New_York` → `America/New York`. */
export function zoneName(tz: string): string {
  return tz.replace(/_/g, ' ');
}

// One offset formatter per zone for the page's lifetime: building ~420 costs
// ~12ms, reusing them ~1ms.
const offsetFormatters = new Map<string, Intl.DateTimeFormat>();

function offsetFormatter(tz: string): Intl.DateTimeFormat {
  let formatter = offsetFormatters.get(tz);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    });
    offsetFormatters.set(tz, formatter);
  }
  return formatter;
}

/**
 * "America/New_York (GMT-4)": the zone plus its offset at `now` (which also
 * shows `Etc/GMT+5` is GMT-5); plain "UTC" for UTC. Just the zone where the
 * engine lacks `shortOffset` (Safari < 15.4, Chrome < 95).
 */
export function zoneLabel(tz: string, now: Date): string {
  if (tz === DEFAULT_TIME_ZONE) return tz;
  const name = zoneName(tz);
  try {
    const offset = offsetFormatter(tz)
      .formatToParts(now)
      .find(p => p.type === 'timeZoneName')?.value;
    return offset ? `${name} (${offset})` : name;
  } catch {
    return name;
  }
}

let browserRaw: string | undefined;
let browserZone = DEFAULT_TIME_ZONE;

/**
 * The browser's own zone, or UTC when the engine reports one it cannot format
 * in (`Etc/Unknown` on a host with no detectable zone).
 */
export function getBrowserTimeZone(): string {
  // Chart axes call this per tick: normalize only when the engine's answer
  // changes (the OS zone can change mid-session).
  const raw = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (raw !== browserRaw) {
    browserRaw = raw;
    browserZone = normalizeTimeZone(raw) ?? DEFAULT_TIME_ZONE;
  }
  return browserZone;
}

/** A selector zone as an IANA zone: 'browser' (or none) is the runtime's own. */
export function resolveTimeZone(tz: string | undefined): string {
  return !tz || tz === 'browser' ? getBrowserTimeZone() : tz;
}
